import { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/shadcn-ui/button';
import { Input } from '@/components/shadcn-ui/input';
import { Label } from '@/components/shadcn-ui/label';
import { Badge } from '@/components/shadcn-ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/shadcn-ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/shadcn-ui/dialog';
import { DataTable } from '@/components/DataTable';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Mail, Key, CheckCircle, XCircle, Clock, Folder, Eye, EyeOff, Search, X, MessageCircle, Settings, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { AdminChatHistory } from '@/components/chat/AdminChatHistory';
import { appAxios } from '@/network/base/appAxios';
import { cn } from '@/lib/utils';
import InfiniteScrollPagination from '@/components/InfiniteScroll';

const PASSWORD_RULES = [
    { id: 'length', label: 'At least 8 characters', test: p => p.length >= 8 },
    { id: 'upper', label: 'One uppercase letter', test: p => /[A-Z]/.test(p) },
    { id: 'lower', label: 'One lowercase letter', test: p => /[a-z]/.test(p) },
    { id: 'number', label: 'One number', test: p => /\d/.test(p) },
    { id: 'special', label: 'One special character', test: p => /[^A-Za-z0-9]/.test(p) },
];

const getStrength = password => PASSWORD_RULES.filter(r => r.test(password)).length;

const STRENGTH_META = [
    { label: 'Very Weak', color: 'bg-red-500' },
    { label: 'Weak', color: 'bg-orange-500' },
    { label: 'Fair', color: 'bg-yellow-500' },
    { label: 'Good', color: 'bg-blue-500' },
    { label: 'Strong', color: 'bg-green-500' },
];

const PasswordStrengthIndicator = ({ password }) => {
    if (!password) return null;
    const score = getStrength(password);
    const meta = STRENGTH_META[score - 1] || STRENGTH_META[0];

    return (
        <div className="space-y-2">
            <div className="flex gap-1">
                {Array.from({ length: 5 }).map((_, i) => (
                    <div
                        key={i}
                        className={cn('h-1 flex-1 rounded-full transition-colors', i < score ? meta.color : 'bg-muted')}
                    />
                ))}
            </div>
            <p className={cn('text-xs font-medium', meta.color.replace('bg-', 'text-'))}>{meta.label}</p>
            <ul className="space-y-1">
                {PASSWORD_RULES.map(rule => (
                    <li key={rule.id} className="flex items-center gap-1.5 text-xs">
                        {rule.test(password) ? (
                            <CheckCircle className="h-3 w-3 text-green-500 shrink-0" />
                        ) : (
                            <XCircle className="h-3 w-3 text-muted-foreground shrink-0" />
                        )}
                        <span className={rule.test(password) ? 'text-foreground' : 'text-muted-foreground'}>
                            {rule.label}
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
};

const CredCard = ({ cred }) => (
    <Card className="overflow-hidden border">
        <CardHeader className="bg-muted/50 pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
                <Folder className="h-5 w-5" />
                {cred.portal_name?.toUpperCase().split('_').join(' ')}
            </CardTitle>
        </CardHeader>
        <CardContent className="pt-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                    <Mail className="h-4 w-4 mt-1 text-muted-foreground" />
                    <div>
                        <div className="text-xs text-muted-foreground">Portal Email</div>
                        <div className="font-medium text-sm break-all">{cred.portal_mail || 'N/A'}</div>
                    </div>
                </div>
                <div className="flex items-start gap-3">
                    <Key className="h-4 w-4 mt-1 text-muted-foreground" />
                    <div>
                        <div className="text-xs text-muted-foreground">Portal Password</div>
                        <div className="font-medium text-sm font-mono">{cred.portal_password || 'N/A'}</div>
                    </div>
                </div>
            </div>

            <div className="pt-2 border-t">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <StatusIndicator label="Signup Status" status={cred.signup_status} />
                    <StatusIndicator label="Profile Status" status={cred.profile_status} />
                </div>
            </div>
        </CardContent>
    </Card>
);

const StatusIndicator = ({ label, status }) => {
    const Icon =
        status === 'success' ? CheckCircle : status === 'pending' ? Clock : XCircle;
    const color =
        status === 'success' ? 'text-green-500' : status === 'pending' ? 'text-yellow-500' : 'text-red-500';

    return (
        <div className="flex items-center gap-2">
            <Icon className={`h-4 w-4 ${color}`} />
            <div>
                <div className="text-xs text-muted-foreground">{label}</div>
                <div className="text-sm font-medium capitalize">{status || 'N/A'}</div>
            </div>
        </div>
    );
};


const USER_TYPE_LABELS = {
    contractor: 'Contractor',
    home_owner: 'Home Owner',
};

const formatDate = dateString => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const CredsManagement = () => {
    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, totalPages: 0 });
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [form, setForm] = useState({ portalMail: '', portalPassword: '' });

    const [ahjs, setAhjs] = useState([]);
    const [ahjsLoading, setAhjsLoading] = useState(false);
    const [ahjPage, setAhjPage] = useState(1);
    const [ahjHasMore, setAhjHasMore] = useState(false);
    const [ahjSearch, setAhjSearch] = useState('');
    const [ahjDropdownOpen, setAhjDropdownOpen] = useState(false);
    const [selectedAhj, setSelectedAhj] = useState(null);

    const ahjSearchRef = useRef('');
    const ahjPageRef = useRef(1);

    const [chatUser, setChatUser] = useState(null);
    const [chats, setChats] = useState([]);
    const [chatsLoading, setChatsLoading] = useState(false);
    const [chatPagination, setChatPagination] = useState({ total: 0, page: 1, limit: 10, totalPages: 0 });

    const [selectedChat, setSelectedChat] = useState(null);

    const fetchUsers = async (page = 1, search = '') => {
        setUsersLoading(true);
        try {
            const params = { page, limit: 10 };
            if (search.trim()) params.search = search.trim();
            const { data } = await appAxios.get('/Admin/v1.0/get-users', { params });
            setUsers(data.data.data || []);
            setPagination(data.data.pagination);
        } catch {
            toast.error('Failed to fetch users');
        } finally {
            setUsersLoading(false);
        }
    };

    useEffect(() => { fetchUsers(1, ''); }, []);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchQuery), 500);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    useEffect(() => { fetchUsers(1, debouncedSearch); }, [debouncedSearch]);

    const fetchAhjs = async (search = '', page = 1, append = false) => {
        ahjSearchRef.current = search;
        ahjPageRef.current = page;
        setAhjsLoading(true);
        try {
            const params = { page, limit: 20 };
            if (search.trim()) params.search = search.trim();
            const { data } = await appAxios.get('/Admin/v1.0/get-all-ahjs', { params });
            const incoming = data.data.data || [];
            setAhjs(prev => (append ? [...prev, ...incoming] : incoming));
            setAhjHasMore(data.data.pagination?.hasNextPage ?? false);
            setAhjPage(page);
        } catch {
            toast.error('Failed to fetch AHJs');
        } finally {
            setAhjsLoading(false);
        }
    };

    const handleAhjLoadMore = useCallback(() => {
        fetchAhjs(ahjSearchRef.current, ahjPageRef.current + 1, true);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (selectedUser) fetchAhjs('', 1, false);
    }, [selectedUser]);

    useEffect(() => {
        if (!dialogOpen) return;
        setAhjs([]);
        setAhjHasMore(false);
        const timer = setTimeout(() => fetchAhjs(ahjSearch, 1, false), 400);
        return () => clearTimeout(timer);
    }, [ahjSearch, dialogOpen]);

    const existingPortalNames = selectedUser?.portal_creds?.map(c => c.portal_name?.toLowerCase()) || [];
    const availableAhjs = ahjs.filter(ahj => !existingPortalNames.includes(ahj.key?.toLowerCase()));

    const resetForm = () => {
        setForm({ portalMail: '', portalPassword: '' });
        setSelectedAhj(null);
        setAhjSearch('');
        setAhjDropdownOpen(false);
        setShowPassword(false);
        setAhjs([]);
        setAhjPage(1);
        setAhjHasMore(false);
    };

    const handleAddCred = async () => {
        if (!selectedAhj || !form.portalMail || !form.portalPassword) {
            toast.error('All fields are required');
            return;
        }
        if (getStrength(form.portalPassword) < 5) {
            toast.error('Password does not meet all requirements');
            return;
        }
        console.log(selectedAhj);

        setSubmitting(true);
        try {
            await appAxios.post('/Admin/v1.0/add-portal-cred', {
                userId: selectedUser._id,
                portalName: selectedAhj.key,
                email: form.portalMail,
                password: form.portalPassword,
                ahj_id: selectedAhj._id,
            });
            toast.success('Credential added successfully');
            setDialogOpen(false);
            resetForm();
            const { data } = await appAxios.get('/Admin/v1.0/get-users', { params: { page: pagination.page, limit: 10 } });
            const updated = (data.data.data || []).find(u => u._id === selectedUser._id);
            setUsers(data.data.data || []);
            if (updated) setSelectedUser(updated);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to add credential');
        } finally {
            setSubmitting(false);
        }
    };

    const fetchChats = async (user, page = 1) => {
        setChatsLoading(true);
        try {
            const { data } = await appAxios.get('/Admin/v1.0/get-chats-by-user-id', {
                params: { userId: user._id, page, limit: 10 },
            });
            const chatsArray = Object.values(data.data.chats || {});
            setChats(chatsArray);
            setChatPagination(data.data.pagination);
        } catch {
            toast.error('Failed to fetch chats');
        } finally {
            setChatsLoading(false);
        }
    };

    const openChatView = user => {
        setChatUser(user);
        setSelectedChat(null);
        fetchChats(user, 1);
    };

    const userColumns = [
        {
            key: 'fullname',
            label: 'Name',
            cellClassName: 'font-medium',
            render: value => value || 'N/A',
        },
        {
            key: 'user_email',
            label: 'Email',
            render: value => <span className="">{value || 'N/A'}</span>,
        },
        {
            key: 'unique_portal_email',
            label: 'System Generated Portal Email',
            render: value => <span className="italic">{value || 'N/A'}</span>,
        },
        {
            key: 'authorization_id',
            label: 'User Type',
            render: value => (
                <Badge variant="outline">{USER_TYPE_LABELS[value?.user_type] || 'N/A'}</Badge>
            ),
        },
        {
            key: 'portal_creds',
            label: 'Creds',
            render: value => <Badge variant="outline">{value?.length || 0} portals</Badge>,
        },
        {
            key: 'actions',
            label: 'Actions',
            render: (_, row) => (
                <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setSelectedUser(row)}>
                        <Settings className="h-4 w-4 mr-2" />
                        Manage Creds
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openChatView(row)}>
                        <MessageCircle className="h-4 w-4 mr-2" />
                        Chat History
                    </Button>
                </div>
            ),
        },
    ];

    if (selectedChat) {
        return (
            <div className="space-y-6">
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="icon" onClick={() => setSelectedChat(null)} className="border rounded-full">
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold tracking-tight">
                            {selectedChat.permitChat.chatName || 'Chat'}
                        </h1>
                        <p className="text-sm text-muted-foreground">
                            {chatUser.fullname} · {formatDate(selectedChat.permitChat.createdAt)}
                        </p>
                    </div>
                    <div className="ml-auto">
                        <Badge variant={selectedChat.permitChat.permitSubmitted ? 'success' : 'destructive'} className={"rounded-md"}>
                            {selectedChat.permitChat.permitSubmitted ? 'Submitted' : 'Not Submitted'}
                        </Badge>
                    </div>
                </div>
                <div className="rounded-lg border p-4 max-h-[70vh] overflow-y-auto no-scrollbar">
                    <AdminChatHistory chatHistory={selectedChat.messages} />
                </div>
            </div>
        );
    }

    if (chatUser) {
        return (
            <div className="space-y-6">
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="icon" onClick={() => { setChatUser(null); setChats([]); }} className="border rounded-full">
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold tracking-tight">{chatUser.fullname || 'N/A'}</h1>
                        <p className="text-sm text-muted-foreground">{chatUser.user_email} · Chat History</p>
                    </div>
                </div>

                {chatsLoading ? (
                    <div className="text-center py-16 text-muted-foreground">Loading chats...</div>
                ) : chats.length === 0 ? (
                    <div className="text-center py-16 text-muted-foreground">No chats found</div>
                ) : (
                    <div className="space-y-3">
                        {chats.map(chat => (
                            <button
                                key={chat.permitChat._id}
                                onClick={() => setSelectedChat(chat)}
                                className="w-full text-left rounded-lg border bg-card p-4 hover:bg-muted/50 transition-colors"
                            >
                                <div className="flex items-center justify-between gap-4">
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium truncate">{chat.permitChat.chatName || 'Untitled'}</div>
                                        <div className="text-xs text-muted-foreground mt-0.5">
                                            {formatDate(chat.permitChat.createdAt)} · {chat.messages.length} message{chat.messages.length !== 1 ? 's' : ''}
                                        </div>
                                    </div>
                                    <Badge variant={chat.permitChat.permitSubmitted ? 'success' : 'destructive'}>
                                        {chat.permitChat.permitSubmitted ? 'Submitted' : 'Not Submitted'}
                                    </Badge>
                                </div>
                            </button>
                        ))}
                    </div>
                )}

                {chatPagination.totalPages > 1 && (
                    <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">
                            Total chats: {chatPagination.total} · Page {chatPagination.page} of {chatPagination.totalPages}
                        </span>
                        <div className="flex gap-2">
                            <Button
                                variant="outline" size="sm"
                                disabled={!chatPagination.hasPrevPage || chatsLoading}
                                onClick={() => fetchChats(chatUser, chatPagination.page - 1)}
                            >
                                <ChevronLeft className="h-4 w-4" /> Previous
                            </Button>
                            <Button
                                variant="outline" size="sm"
                                disabled={!chatPagination.hasNextPage || chatsLoading}
                                onClick={() => fetchChats(chatUser, chatPagination.page + 1)}
                            >
                                Next <ChevronRight className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    if (selectedUser) {
        const creds = selectedUser.portal_creds || [];
        return (
            <div className="space-y-6">
                <div className="flex flex-col md:flex-row items-center justify-between gap-3" >
                    <div className="flex items-center gap-3">
                        <Button variant="ghost" size="icon" onClick={() => setSelectedUser(null)} className="border rounded-full">
                            <ArrowLeft className="h-5 w-5" />
                        </Button>
                        <div>
                            <h1 className="text-2xl font-bold tracking-tight">{selectedUser.fullname || 'N/A'}</h1>
                            <p className="text-sm text-muted-foreground">{selectedUser.user_email}</p>
                        </div>
                    </div>
                    <Button onClick={() => { resetForm(); setDialogOpen(true); }}>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Credential
                    </Button>
                </div>

                <div className="rounded-lg border p-4">
                    {creds.length === 0 ? (
                        <div className="text-center py-16 text-muted-foreground">No portal credentials yet</div>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {creds.map((cred, i) => (
                                <CredCard key={cred._id || i} cred={cred} />
                            ))}
                        </div>
                    )}
                </div>

                <Dialog open={dialogOpen} onOpenChange={open => { setDialogOpen(open); if (!open) resetForm(); }}>

                    <DialogContent className="sm:max-w-xl">
                        <DialogHeader>
                            <DialogTitle>Add Portal Credential</DialogTitle>
                            <DialogDescription>
                                Select an AHJ portal and enter the login credentials for{' '}
                                <span className="font-medium text-foreground">{selectedUser?.fullname}</span>.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-5">
                            <div className="space-y-2">
                                <Label>AHJ / Portal</Label>
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none z-10" />
                                    <Input
                                        className="pl-9 pr-9"
                                        placeholder="Search AHJ by name..."
                                        value={ahjSearch}
                                        onChange={e => { setAhjSearch(e.target.value); setSelectedAhj(null); }}
                                        onFocus={() => setAhjDropdownOpen(true)}
                                        onClick={() => setAhjDropdownOpen(true)}
                                    />
                                    {ahjSearch ? (
                                        <button
                                            type="button"
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                            onClick={() => { setAhjSearch(''); setSelectedAhj(null); }}
                                        >
                                            <X className="h-4 w-4" />
                                        </button>
                                    ) : (
                                        <ChevronDown className={cn('absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground transition-transform pointer-events-none', ahjDropdownOpen && 'rotate-180')} />
                                    )}

                                    {ahjDropdownOpen && (
                                        <>
                                            <div className="fixed inset-0 z-40" onClick={() => setAhjDropdownOpen(false)} />
                                            <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg overflow-hidden">
                                                <div className="max-h-52 overflow-y-auto">
                                                    {ahjsLoading && ahjs.length === 0 ? (
                                                        <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                                                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
                                                            Loading AHJs...
                                                        </div>
                                                    ) : availableAhjs.length === 0 ? (
                                                        <div className="py-6 text-center text-sm text-muted-foreground">
                                                            {ahjSearch ? `No results for "${ahjSearch}"` : 'No AHJs available'}
                                                        </div>
                                                    ) : (
                                                        <>
                                                            {availableAhjs.map(ahj => (
                                                                <button
                                                                    key={ahj._id}
                                                                    type="button"
                                                                    className={cn(
                                                                        'w-full text-left px-3 py-2.5 text-sm transition-colors',
                                                                        'hover:bg-muted/50',
                                                                        selectedAhj?._id === ahj._id && 'bg-muted font-medium',
                                                                    )}
                                                                    onClick={() => {
                                                                        setSelectedAhj(ahj);
                                                                        setAhjSearch(ahj.name);
                                                                        setAhjDropdownOpen(false);
                                                                    }}
                                                                >
                                                                    <span className="block font-medium">{ahj.name}</span>

                                                                </button>
                                                            ))}
                                                            <InfiniteScrollPagination
                                                                hasMore={ahjHasMore}
                                                                loading={ahjsLoading}
                                                                onLoadMore={handleAhjLoadMore}
                                                                loadingText="Loading more..."
                                                                showEndText={false}
                                                                threshold={40}
                                                                className="py-0"
                                                            />
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label>Portal Email</Label>
                                <Input
                                    type="email"
                                    placeholder="portal@example.com"
                                    value={form.portalMail}
                                    onChange={e => setForm(f => ({ ...f, portalMail: e.target.value }))}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>Portal Password</Label>
                                <div className="relative">
                                    <Input
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="Strong password"
                                        value={form.portalPassword}
                                        onChange={e => setForm(f => ({ ...f, portalPassword: e.target.value }))}
                                        autoComplete="new-password"
                                        className="pr-10"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(v => !v)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                    >
                                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </button>
                                </div>
                                <PasswordStrengthIndicator password={form.portalPassword} />
                            </div>
                        </div>

                        <DialogFooter className="border-t pt-4">
                            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                            <Button onClick={handleAddCred} disabled={submitting}>
                                {submitting ? 'Adding...' : 'Add Credential'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Credentials Management</h1>
                    <p className="text-muted-foreground">
                        {debouncedSearch
                            ? `Found ${pagination.total} result${pagination.total !== 1 ? 's' : ''} for "${debouncedSearch}"`
                            : 'Manage portal credentials for users'}
                    </p>
                </div>
                <div className="relative w-72">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        placeholder="Search by name or email..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        className="pl-9 pr-9"
                    />
                    {usersLoading && searchQuery && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
                        </div>
                    )}
                    {searchQuery && !usersLoading && (
                        <button
                            onClick={() => setSearchQuery('')}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    )}
                </div>
            </div>
            <DataTable
                data={users}
                columns={userColumns}
                loading={usersLoading}
                searchable={false}
                pagination={pagination}
                onPageChange={page => fetchUsers(page, debouncedSearch)}
                emptyMessage="No users found"
            />
        </div>
    );
};

export default CredsManagement;
